/**
 * ShareResult — "You're all done!" result card molecule.
 *
 * Matches WeTransfer's world-class minimalist result screen:
 * - Preview sub-card (eye icon + "View transfer details")
 * - Celebratory "You're all done!" title
 * - "Copy your transfer link below or share through a QR code" subtitle
 * - Direct BitDrop link copy row with stateful "Copy" -> "Copied!" (2s)
 * - Secondary Magnet link copy row with stateful "Copy" -> "Copied!" (2s)
 * - Download .torrent button
 * - Clean "Send another file" reset button
 * - Modal QR Code view for seamless phone camera scanning
 */
import React, { useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Modal, TouchableWithoutFeedback } from 'react-native';
import { useTheme } from '../hooks/useTheme';
import { Icon } from '../atoms/Icon';
import { QRCode } from './QRCode';
import { triggerBrowserDownload } from '../../../platform/browser-download';
import { Spacing, FontSize, FontWeight, Radius, Shadows } from '../theme/tokens';
import { AppConfig } from '../../../shared/config/app-config';

// ─── Props ───────────────────────────────────────────────────────────────────

export interface ShareResultProps {
  shareUrl: string;
  shareHash?: string | null;
  torrentName?: string;
  torrentFile?: Uint8Array;
  onCopy?: (url: string) => void;
  onCloseShare?: () => void;
  onViewDetails?: () => void;
}

// ─── Component ───────────────────────────────────────────────────────────────

export const ShareResult: React.FC<ShareResultProps> = ({
  shareUrl,
  shareHash,
  torrentName,
  torrentFile,
  onCopy,
  onCloseShare,
  onViewDetails,
}) => {
  const { colors } = useTheme();
  const [showQR, setShowQR] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedMagnet, setCopiedMagnet] = useState(false);

  const bitdropUrl = shareHash ? `${AppConfig.shareBaseUrl}/${shareHash}` : shareUrl;

  const handleCopyLink = () => {
    if (onCopy) onCopy(bitdropUrl);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const handleCopyMagnet = () => {
    if (onCopy) onCopy(shareUrl);
    setCopiedMagnet(true);
    setTimeout(() => setCopiedMagnet(false), 2000);
  };

  const handleDownloadTorrent = () => {
    if (typeof document === 'undefined' || typeof Blob === 'undefined' || !torrentFile) return;
    const blob = new Blob([torrentFile as any], { type: 'application/x-bittorrent' });
    const safeName = torrentName ? torrentName.replace(/[^a-z0-9 _.-]/gi, '').trim() : 'download';
    const finalName = safeName.endsWith('.torrent') ? safeName : `${safeName}.torrent`;
    triggerBrowserDownload(blob, finalName);
  };

  return (
    <View style={styles.container}>
      {/* ── Preview Sub-Card (View transfer details) ──────────────── */}
      {onViewDetails && (
        <TouchableOpacity
          style={[styles.previewCard, { backgroundColor: colors.pill, borderColor: colors.border }]}
          onPress={onViewDetails}
          activeOpacity={0.85}
        >
          <View style={[styles.previewBtn, { backgroundColor: colors.surface, borderColor: colors.border }]}>
            <Icon name="eye" size={14} color={colors.text as string} strokeWidth={2} />
            <Text style={[styles.previewBtnLabel, { color: colors.text }]}>
              View transfer details
            </Text>
          </View>
        </TouchableOpacity>
      )}

      {/* ── Celebratory Header ──────────────────────────────────────── */}
      <Text style={[styles.title, { color: colors.text }]}>
        You're all done!
      </Text>

      <Text style={[styles.subtext, { color: colors.subtext }]}>
        {'Copy your transfer link below or share through a '}
        <Text
          style={[styles.link, { color: colors.text }]}
          onPress={() => setShowQR(true)}
        >
          QR code
        </Text>
      </Text>

      {/* ── Copy Action Boxes ────────────────────────────────────────── */}
      <View style={styles.actions}>
        {/* Direct BitDrop link */}
        <View style={[styles.copyRow, { borderColor: colors.border, backgroundColor: colors.surface }]}>
          <Text style={[styles.copyText, { color: colors.text }]} numberOfLines={1}>
            {bitdropUrl}
          </Text>
          <TouchableOpacity
            style={[
              styles.copyBtn,
              {
                borderLeftColor: colors.border,
                backgroundColor: copiedLink ? colors.brand : colors.pill,
              },
            ]}
            onPress={handleCopyLink}
            activeOpacity={0.8}
          >
            <Icon
              name={copiedLink ? 'check' : 'link'}
              size={13}
              color={copiedLink ? (colors.brandText as string) : (colors.text as string)}
              strokeWidth={2.5}
            />
            <Text
              style={[
                styles.copyBtnText,
                { color: copiedLink ? colors.brandText : colors.text },
              ]}
            >
              {copiedLink ? 'Copied!' : 'Copy'}
            </Text>
          </TouchableOpacity>
        </View>

        {/* Magnet link row */}
        <View style={[styles.copyRow, { borderColor: colors.border, backgroundColor: colors.surface }]}>
          <View style={styles.magnetIconWrap}>
            <Icon name="magnet" size={14} color={colors.subtext as string} strokeWidth={2} />
          </View>
          <Text style={[styles.copyText, { color: colors.subtext }]} numberOfLines={1}>
            {shareUrl}
          </Text>
          <TouchableOpacity
            style={[
              styles.copyBtn,
              {
                borderLeftColor: colors.border,
                backgroundColor: copiedMagnet ? colors.brand : colors.pill,
              },
            ]}
            onPress={handleCopyMagnet}
            activeOpacity={0.8}
          >
            <Icon
              name={copiedMagnet ? 'check' : 'copy'}
              size={13}
              color={copiedMagnet ? (colors.brandText as string) : (colors.text as string)}
              strokeWidth={2.5}
            />
            <Text
              style={[
                styles.copyBtnText,
                { color: copiedMagnet ? colors.brandText : colors.text },
              ]}
            >
              {copiedMagnet ? 'Copied!' : 'Copy'}
            </Text>
          </TouchableOpacity>
        </View>

        {/* Download .torrent CTA button */}
        {torrentFile && (
          <TouchableOpacity
            style={[styles.downloadBtn, { backgroundColor: colors.brand }]}
            onPress={handleDownloadTorrent}
            activeOpacity={0.85}
          >
            <Icon name="download" size={16} color={colors.brandText as string} strokeWidth={2.5} />
            <Text style={[styles.downloadBtnText, { color: colors.brandText }]}>
              Download .torrent
            </Text>
          </TouchableOpacity>
        )}
      </View>

      {/* ── Send Another File Reset Action ─────────────────────────── */}
      <TouchableOpacity
        style={styles.startAnotherBtn}
        onPress={onCloseShare}
        activeOpacity={0.7}
      >
        <Text style={[styles.startAnotherText, { color: colors.subtext }]}>
          Send another file
        </Text>
      </TouchableOpacity>

      {/* ── Modal QR Code View ──────────────────────────────────────── */}
      <Modal
        visible={showQR}
        transparent
        animationType="fade"
        onRequestClose={() => setShowQR(false)}
      >
        <TouchableWithoutFeedback onPress={() => setShowQR(false)}>
          <View style={styles.qrOverlay}>
            <TouchableWithoutFeedback>
              <View
                style={[
                  styles.qrCard,
                  { backgroundColor: colors.surface, borderColor: colors.border },
                ]}
              >
                <TouchableOpacity
                  onPress={() => setShowQR(false)}
                  style={styles.qrClose}
                  hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                >
                  <Icon name="close" size={20} color={colors.subtext as string} strokeWidth={2} />
                </TouchableOpacity>

                <Text style={[styles.qrTitle, { color: colors.text }]}>
                  Scan to Download
                </Text>

                <View style={[styles.qrCodeWrap, { backgroundColor: colors.surface }]}>
                  <QRCode
                    value={bitdropUrl}
                    size={190}
                    color={colors.text as string}
                    backgroundColor={colors.surface as string}
                  />
                </View>

                <Text style={[styles.qrSubtext, { color: colors.subtext }]}>
                  Point your phone's camera at this QR code to download the transfer directly.
                </Text>
              </View>
            </TouchableWithoutFeedback>
          </View>
        </TouchableWithoutFeedback>
      </Modal>
    </View>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
    width:      '100%',
    paddingTop: Spacing.xs,
  },

  // Preview sub-card
  previewCard: {
    width:           '100%',
    height:          88,
    borderRadius:    Radius.xl,
    borderWidth:     1,
    marginBottom:    Spacing.lg,
    alignItems:      'center',
    justifyContent:  'center',
  },
  previewBtn: {
    flexDirection:     'row',
    alignItems:        'center',
    gap:               6,
    paddingHorizontal: Spacing.md,
    paddingVertical:   7,
    borderRadius:      Radius.pill,
    borderWidth:       1,
  } as any,
  previewBtnLabel: {
    fontSize:      FontSize.xs + 1,
    fontWeight:    FontWeight.semibold,
    letterSpacing: -0.1,
  },

  // Header Title & Subtitle
  title: {
    fontSize:      22,
    fontWeight:    FontWeight.bold,
    lineHeight:    28,
    marginBottom:  6,
    letterSpacing: -0.4,
    textAlign:     'center',
  },
  subtext: {
    fontSize:      FontSize.sm,
    lineHeight:    20,
    textAlign:     'center',
    letterSpacing: -0.1,
    paddingHorizontal: Spacing.xs,
  },
  link: {
    fontSize:           FontSize.sm,
    lineHeight:         20,
    fontWeight:         FontWeight.bold,
    textDecorationLine: 'underline',
  },

  // Copy rows
  actions: {
    width:     '100%',
    gap:       10,
    marginTop: Spacing.lg,
  } as any,
  copyRow: {
    flexDirection: 'row',
    alignItems:    'center',
    height:        44,
    borderWidth:   1,
    borderRadius:  Radius.pill,
    overflow:      'hidden',
  },
  magnetIconWrap: {
    paddingLeft: Spacing.sm + 2,
    alignItems:  'center',
    justifyContent: 'center',
  },
  copyText: {
    flex:              1,
    paddingHorizontal: Spacing.sm + 4,
    fontSize:          FontSize.xs + 1,
    letterSpacing:     -0.1,
  },
  copyBtn: {
    flexDirection:   'row',
    alignItems:      'center',
    justifyContent:  'center',
    paddingHorizontal: Spacing.md,
    height:          '100%',
    borderLeftWidth: 1,
    gap:             6,
    minWidth:        76,
  } as any,
  copyBtnText: {
    fontSize:      FontSize.xs + 1,
    fontWeight:    FontWeight.bold,
    letterSpacing: -0.1,
  },

  // Download .torrent CTA
  downloadBtn: {
    flexDirection:  'row',
    height:         48,
    borderRadius:   Radius.pill,
    justifyContent: 'center',
    alignItems:     'center',
    width:          '100%',
    gap:            8,
    marginTop:      Spacing.xs,
  } as any,
  downloadBtnText: {
    fontSize:      FontSize.sm + 1,
    fontWeight:    FontWeight.bold,
    letterSpacing: -0.2,
  },

  // Send another file
  startAnotherBtn: {
    marginTop:       Spacing.lg,
    paddingVertical: Spacing.xs,
  },
  startAnotherText: {
    fontSize:           FontSize.sm,
    fontWeight:         FontWeight.semibold,
    letterSpacing:      -0.1,
    textDecorationLine: 'underline',
  },

  // QR Modal
  qrOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0, 0, 0, 0.45)',
    justifyContent:  'center',
    alignItems:      'center',
    zIndex:          9999,
  } as any,
  qrCard: {
    padding:       Spacing.xl,
    borderRadius:  Radius.xxl,
    width:         320,
    maxWidth:      '90%' as any,
    alignItems:    'center',
    borderWidth:   1,
    ...Shadows.card,
  },
  qrClose: {
    position: 'absolute',
    top:      16,
    right:    16,
    padding:  4,
  },
  qrTitle: {
    fontSize:      17,
    fontWeight:    FontWeight.bold,
    letterSpacing: -0.3,
    marginBottom:  Spacing.md,
  },
  qrCodeWrap: {
    padding:       Spacing.md,
    borderRadius:  Radius.lg,
    marginBottom:  Spacing.md,
    alignItems:    'center',
    justifyContent: 'center',
  },
  qrSubtext: {
    fontSize:          FontSize.xs + 1,
    textAlign:         'center',
    lineHeight:        18,
    paddingHorizontal: Spacing.sm,
    letterSpacing:     -0.1,
  },
});
